import { useTranslation } from "react-i18next";
import { getCarrierLabel } from "@babackapp/carriers-utils";
import { useBlockPanelPropsContext } from "../../lib/panel/panel-container";
import {
  BON_DE_RETOUR_CONTENT_KEY,
  CARRIER_TO_INSTRUCTION,
  INSTRUCTION_BY_ID,
  type InstructionKey,
} from "./instructions";

/**
 * Pseudo-carriers keep their translated panel labels: `getCarrierLabel` yields
 * English registry names ("Customer Carrier") for customer/merchant carriers and
 * the raw code for `return-to-store` (absent from the carriers registry).
 */
const PSEUDO_CARRIER_INSTRUCTION_KEYS: InstructionKey[] = [
  "customerCarrier",
  "merchantCarrier",
  "returnToStore",
];

export type CarrierMessageOption = { value: string; label: string };

/**
 * Dropdown options for the shipping-instruction override panels: one entry per
 * shop carrier (from the `return.carrierCode` variable options, the same list
 * the variables panel uses) plus a fixed "return slip" entry. Panel-only —
 * `getCarrierLabel` is kept out of the email render path.
 */
export function useCarrierMessageOptions(): CarrierMessageOption[] {
  const { t } = useTranslation("editor-blocks-library");
  const { variables } = useBlockPanelPropsContext();
  const carrierOptions =
    variables?.find((v) => v.id === "return.carrierCode")?.options ?? [];

  const options: CarrierMessageOption[] = carrierOptions
    // Defensive: a carrier code colliding with the return-slip key would corrupt
    // that override; the return slip is added as a dedicated entry below.
    .filter((o) => o.value !== BON_DE_RETOUR_CONTENT_KEY)
    .map((o) => {
      const instructionKey = CARRIER_TO_INSTRUCTION[o.value];
      const isPseudoCarrier =
        instructionKey &&
        PSEUDO_CARRIER_INSTRUCTION_KEYS.includes(instructionKey);
      const label = isPseudoCarrier
        ? t(
            INSTRUCTION_BY_ID[instructionKey].labelKey,
            INSTRUCTION_BY_ID[instructionKey].labelFallback,
          )
        : getCarrierLabel(o.value);
      return { value: o.value, label };
    });

  options.push({
    value: BON_DE_RETOUR_CONTENT_KEY,
    label: t(
      INSTRUCTION_BY_ID.bonDeRetour.labelKey,
      INSTRUCTION_BY_ID.bonDeRetour.labelFallback,
    ),
  });

  return options;
}
